import type {
  IFacebookPixelTrackPurchaseArgs,
  IFacebookPixelTrackViewProductDetailsArgs,
} from '~gateways/analytics/facebook-pixel-service';

type AnalyticsEventsDataTypes = {
  view_brand_concession: {
    brandId: number;
    brandName: string;
    productId?: number;
    userId?: number;
  };

  view_brand_section: {
    userId?: number;
    brandId: number;
    brandName: string;
    sectionId: number;
    sectionName: string;
  };

  view_product_set: {
    userId?: number;
    productSetId: number;
    productSetName: string;
    productId?: number;
  };

  view_product_details: {
    userId?: number;
    productId: number;
    productName: string;
    // here may we need to add other props as 'Context' by a future decision
  };

  view_department: {
    userId?: number;
    departmentId: number;
    departmentName: string;
    floorId: string;
    floorName: string;
  };

  browse_floor: {
    userId?: number;
    floorId: number;
    floorName: string;
  };

  view_floor: {
    userId?: number;
    departmentId?: number;
    departmentName?: string;
    floorId: string;
    floorName: string;
  };

  view_personal: {
    userId: number;
  };

  product_action_buy: {
    userId?: number;
    productId?: number;
    productName?: string;
  };

  app_start: {
    userId?: number;
  };

  // The following events have different capitalization to be kept compatible with
  // legacy events in the backend

  referral: {
    // product action visit
    userId?: number;
    productId: number;
    productName: string;
  };

  sign_up: {
    userId?: number;
    method: SignUpMethods;
  };

  begin_checkout: {
    userId?: number;
    productId: number;
    itemName: string;
    itemOptionId: number;
    itemBrand: string;
    itemPrice: number;
    itemQuantity: number;
    productTypeName?: string;
    subtotal: number;
  };

  purchase: {
    userId?: number;
    postage?: number;
    orderId?: number;
    subtotal?: number;
    productId: number;
    productName?: string;
    productOptionId?: number;
    productType?: string;
    price?: number;
    quantity: number;
  };

  abandon_checkout: {
    userId?: number;
    reason: AbandonCheckoutReason2;
    step: CheckoutStep;
  };

  login: {
    userId: number;
    context: LoginContext;
  };
};

export type AnalyticsEvent = keyof AnalyticsEventsDataTypes;

export type AnalyticsEventData<T extends AnalyticsEvent> =
  AnalyticsEventsDataTypes[T];

export type EventLogParameters<T extends AnalyticsEvent> = {
  event: T;
  data: AnalyticsEventsDataTypes[T];
};

export type SignUpMethods =
  | 'checkout'
  | 'favourite'
  | 'history'
  | 'login'
  | 'orders'
  | 'saved'
  | 'share'
  | 'smartbrowsing';

export type CheckoutStep =
  | 'identify_prompt'
  | 'logging_in'
  | 'none'
  | 'ordering'
  | 'post_order'
  | 'preview_summary';

export type AbandonCheckoutReason2 =
  | 'cancel_button'
  | 'close_window'
  | 'journey_change'
  | 'product_change';

type LoginContext =
  | 'favourite'
  | 'history'
  | 'incheckout'
  | 'login'
  | 'orders'
  | 'saved'
  | 'share';

export enum FirebaseLegacyAnalyticsEvents {
  abandonCheckout = 'abandon_checkout',
  share = 'share',
  signUp = 'sign_up',
  login = 'login',
  referral = 'referral',
  wishlist = 'add_to_wishlist',
  beginCheckout = 'begin_checkout',
  info = 'info',
}

export type AnalyticsSignUpEvent = {
  user: number | string;
  method: SignUpMethods;
};

export type AnalyticsLoginEvent = {
  user: number | string;
  method: LoginContext;
};

export type AnalyticsWishlistEvent = ProductAnalytics & {
  user: number | string;
};

export type AnalyticsReferralEvent = ProductAnalytics & {
  user: number | string;
};

export type AnalyticsShareEvent = ProductAnalytics & {
  user: number | string;
  type: string;
  method: string;
};

export type AnalyticsPurchaseEvent = ProductAnalytics & {
  user: number | string;
  shipping?: string | undefined;
  transactionId?: number;
  value?: number | string;
};

export type AnalyticsAbandonCheckoutEvent = {
  user: number | string;
  method: AbandonCheckoutReason2;
  context: CheckoutStep;
};

export type AnalyticsBeginCheckoutEvent = {
  user: number | string;
  totalOrderValueWithoutPostage: number;
  productId: number | string;
  productName: string;
  productVariant: number;
  productBrand: string;
  productCategory?: string;
  productPrice: number;
  quantity: number;
};

export enum LoginMethods {
  // noinspection JSUnusedGlobalSymbols
  incheckout = 'incheckout',
  favourite = 'favourite',
  saved = 'saved',
  orders = 'orders',
  history = 'history',
  share = 'share',
  login = 'login',
}
type ProductAnalytics = {
  productId?: number | string;
  productName?: string;
  productBrand?: string;
  productPrice?: number | string;
  productCategory?: string | null;
  currency?: string;
  quantity?: number;
  productOption?: number | string | null;
};

export const mapBeginCheckoutDataToFirebase = (
  data: AnalyticsEventData<'begin_checkout'>
): AnalyticsBeginCheckoutEvent => ({
  user: data.userId ?? '',
  productId: data.productId,
  productName: data.itemName,
  productVariant: data.itemOptionId,
  productBrand: data.itemBrand,
  productPrice: data.itemPrice,
  quantity: data.itemQuantity,
  productCategory: data.productTypeName,
  totalOrderValueWithoutPostage: data.subtotal,
});

export const mapBeginCheckoutDataToFacebookPixel = (
  data: AnalyticsEventData<'begin_checkout'>
): IFacebookPixelTrackPurchaseArgs => ({
  contentIds: [data.productId.toString()],
  contentName: data.itemName,
  value: data.subtotal,
  contents: [{ id: data.productId.toString(), quantity: data.subtotal }],
});

export const mapPurchaseDataToFirebase = (
  data: AnalyticsEventData<'purchase'>
): AnalyticsPurchaseEvent => ({
  user: data.userId ?? '',
  shipping: data.postage?.toFixed(2),
  transactionId: data.orderId,
  value: data.subtotal,
  productId: data.productId,
  productName: data.productName,
  productOption: data.productOptionId,
  productCategory: data.productType,
  productPrice: data.price,
  quantity: data.quantity,
});

export const mapPurchaseDataToFacebookPixel = (
  data: AnalyticsEventData<'purchase'>
): IFacebookPixelTrackPurchaseArgs => ({
  contentIds: [data.productId.toString()],
  contentName: data.productName ?? null,
  value: data.subtotal ?? null,
  contents: [
    {
      id: data.productId.toString(),
      quantity: data.quantity,
    },
  ],
});

export const mapViewProductDetailsDataToFacebookPixel = (
  data: AnalyticsEventData<'view_product_details'>
): IFacebookPixelTrackViewProductDetailsArgs => ({
  contentIds: [data.productId.toString()],
  contentName: data.productName ?? null,
});
